import { useEffect, useRef } from 'react';

function Hero() {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    // Trigger animations on mount
    const timer = setTimeout(() => {
      if (titleRef.current) titleRef.current.classList.add('animate-fade-in-up');
      setTimeout(() => {
        if (subtitleRef.current) subtitleRef.current.classList.add('animate-fade-in-up');
        setTimeout(() => {
          if (buttonRef.current) buttonRef.current.classList.add('animate-fade-in-up');
          setTimeout(() => {
            if (videoRef.current) videoRef.current.classList.add('animate-fade-in-scale');
          }, 200);
        }, 200);
      }, 200);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative w-full pt-20 overflow-visible">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-visible pointer-events-none">
        {/* Large floating circles */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute top-40 left-10 w-96 h-96 bg-primary-300/20 rounded-full blur-3xl animate-float-medium"></div>
        <div className="absolute bottom-40 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-float-fast"></div>
        
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-gradient-to-br from-white/20 to-primary-200/20 rounded-full blur-2xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/3 right-1/3 w-56 h-56 bg-gradient-to-br from-primary-200/15 to-white/15 rounded-full blur-2xl animate-pulse-medium"></div>
      </div>

      {/* Floating Payment Icons */}
      <div className="absolute inset-0 pointer-events-none overflow-visible">
        {/* Credit Card Icon */}
        <div className="absolute top-32 left-4 md:left-20 animate-float-icon-1">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
            <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        </div>
        
        {/* Money Icon */}
        <div className="absolute top-48 right-4 md:right-32 animate-float-icon-2">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
            <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        
        {/* Shield/Check Icon */}
        <div className="absolute bottom-64 left-4 md:left-1/4 animate-float-icon-3">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
            <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
        
        {/* Zap/Lightning Icon */}
        <div className="absolute top-64 right-4 md:right-1/3 animate-float-icon-4">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
            <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        </div>

        {/* Additional Floating Icons - Hidden on mobile, shown on desktop */}
        {/* Shopping Cart */}
        <div className="hidden md:block absolute top-20 right-1/3 animate-float-icon-5">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>

        {/* Chart/Graph Icon */}
        <div className="hidden md:block absolute bottom-32 right-20 animate-float-icon-6">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>

        {/* Lock Icon */}
        <div className="hidden md:block absolute top-1/2 left-16 animate-float-icon-7">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        {/* Star Icon */}
        <div className="hidden md:block absolute bottom-48 left-1/3 animate-float-icon-8">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
        </div>

        {/* Globe/World Icon */}
        <div className="hidden md:block absolute top-80 left-1/2 animate-float-icon-9">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Check Circle Icon */}
        <div className="hidden md:block absolute bottom-80 right-1/4 animate-float-icon-10">
          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl shadow-lg">
            <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Floating Particles/Dots */}
        {[...Array(12)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className={`absolute w-2 h-2 bg-white/30 rounded-full animate-float-particle`}
            style={{
              left: `${10 + (i * 7)}%`,
              top: `${15 + (i * 6)}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${4 + (i % 3)}s`
            }}
          />
        ))}
      </div>

      {/* Content Section */}
      <div className="relative px-4 py-16 md:py-24 text-center z-10">
        {/* Glowing title effect */}
        <div className="relative inline-block">
          <h1 
            ref={titleRef}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 opacity-0 translate-y-8 relative z-10 drop-shadow-2xl"
            style={{ textShadow: '0 0 30px rgba(255,255,255,0.5)' }}
          >
            جهاز الدفع
          </h1>
          {/* Glow effect behind title */}
          <div className="absolute inset-0 blur-2xl bg-white/30 opacity-0 animate-glow"></div>
        </div>
        
        {/* Subtitle with subtle animation */}
        <p 
          ref={subtitleRef}
          className="text-lg md:text-xl lg:text-2xl text-white mb-8 max-w-3xl mx-auto px-4 opacity-0 translate-y-8 drop-shadow-lg"
        >
          مهما كان حجم عملك، استقبل المدفوعات بالطريقة اللي تناسبك
        </p>
        
        {/* Enhanced CTA Button with glow */}
        <div className="relative inline-block">
          <a 
            ref={buttonRef}
            href="#products"
            className="relative bg-white text-primary-500 px-8 py-4 rounded-full text-lg md:text-xl font-semibold hover:bg-primary-50 transition-all duration-300 hover:scale-105 hover:shadow-2xl opacity-0 translate-y-8 z-10 overflow-hidden group inline-block no-underline"
          >
            <span className="relative z-10">ابدأ رحلتك الان</span>
            {/* Shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
          </a>
          {/* Button glow */}
          <div className="absolute inset-0 bg-white/50 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>

        {/* Stats or Features Pills */}
        <div className="mt-12 flex flex-wrap justify-center gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
          <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full text-white text-sm md:text-base font-medium border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105 flex items-center gap-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>دفع سريع</span>
          </div>
          <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full text-white text-sm md:text-base font-medium border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105 flex items-center gap-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>آمن ومضمون</span>
          </div>
          <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full text-white text-sm md:text-base font-medium border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105 flex items-center gap-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span>جميع البطاقات</span>
          </div>
        </div>
      </div>
      
      {/* Video Section: smaller, ~30% overlaps into next section, fully rounded */}
      <div className="relative w-full px-3 sm:px-6 z-10 pb-8 md:pb-12">
        <div 
          className="relative w-full mx-auto overflow-visible lg:max-w-[55%]"
          style={{ maxWidth: '75%' }}
        >
          {/* Glow above video */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary-500/20 via-transparent to-transparent rounded-[2rem] blur-2xl pointer-events-none" />
          
          <div 
            ref={videoRef}
            className="relative w-full bg-gray-200 rounded-[2rem] overflow-hidden opacity-0 scale-95 shadow-2xl border-4 border-white/20 min-h-[28vh] md:min-h-[36vh] lg:min-h-[34vh] -mb-[10vh] md:-mb-[12vh] lg:-mb-[11vh]"
            style={{ aspectRatio: '16/9', maxWidth: '100%' }}
          >
            {/* Gradient overlay on video */}
            <div className="absolute inset-0 bg-gradient-to-t from-primary-500/10 via-transparent to-transparent z-10 pointer-events-none" />
            
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="/videos/hero_video.mov" type="video/quicktime" />
              <source src="/videos/hero_video.mov" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-6">
                <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
